/**
 * Email trust helpers.
 *
 * Encourages (but does not require) students to register with a .edu address.
 * A .edu domain is treated as a **soft** trust signal — never a substitute
 * for real NSC verification, per policy.
 *
 * Exposes:
 *   parseEmail(email) -> { domain, tld, sld, isEdu, looksEducational, isEmpty }
 *
 * "looksEducational" catches .edu.* variants (UK's .ac.uk equivalents can be
 * added later — for now, kept US-focused where NSC operates).
 */

// Registrar-defined TLDs where enrollment can be reasonably attested by the
// email domain alone. Kept intentionally short — anything not on this list
// only becomes trusted through NSC.
const EDU_TLDS = ["edu"];
// International equivalents that MAY warrant a softer badge in future
// (currently informational only — treated the same as any other domain).
const EDU_HINTS = ["ac.uk", "edu.au", "edu.sg", "ac.nz", "edu.mx"];

export const parseEmail = (raw = "") => {
  const email = String(raw || "").trim().toLowerCase();
  if (!email || !email.includes("@")) {
    return { domain: "", tld: "", sld: "", isEdu: false, looksEducational: false, isEmpty: true };
  }
  const [, domain] = email.split("@");
  const parts = domain.split(".");
  const tld = parts[parts.length - 1] || "";
  const sld = parts.length > 1 ? parts[parts.length - 2] : "";
  const isEdu = EDU_TLDS.includes(tld);
  const looksEducational = isEdu || EDU_HINTS.some((h) => domain.endsWith(`.${h}`));
  return { domain, tld, sld, isEdu, looksEducational, isEmpty: false };
};

export const isValidEmail = (raw = "") => {
  const email = String(raw || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};
